/**
 * MapControllers
 * 
 * @atomic - Handles all map controller components
 * @idempotent - Pure component with React.memo
 * @modular - Consolidates map interaction controllers
 * 
 * Purpose: Renders all Leaflet map controllers (bounds fitting, popups, clicks, etc.)
 * Props: All controller-related props
 * Returns: Group of controller components
 */

import { memo } from 'react'
import { MapController, MapBoundsFitter, MapClickHandler, PopupHandler } from '../../pages/map/ControlComponents'
import MapDragHandler from './MapDragHandler'

const MapControllers = memo(function MapControllers({
  // MapController props
  mapRef,
  
  // PopupHandler props
  isImmersiveMode,
  onTabChange,
  isSearchPanelMinimized,
  setIsSearchPanelMinimized,
  
  // MapClickHandler props
  isSpotModeActive,
  onLocationSelect,
  setFocusedEntityId,
  setIsolatedEntityId,
  userLocation,
  spotModeLocation,
  setSpotModeLocationSetTime,
  
  // MapBoundsFitter props
  markers,
  isEntityMode,
  radius,
  panelState,
  leftPanelWidth = 0,
  
  // MapDragHandler props
  enableDragToLoad = false,
  fetchNearbyEntities,
  reverseGeocode,
  setUserLocation,
  setSearchLocationName,
  setSearchLocation,
  saveLocation,
  t
}) {
  return (
    <>
      <MapController key="map-controller" mapRef={mapRef} />
      
      <PopupHandler 
        key="popup-handler"
        isImmersiveMode={isImmersiveMode}
        onTabChange={onTabChange}
        isSearchPanelMinimized={isSearchPanelMinimized}
        setIsSearchPanelMinimized={setIsSearchPanelMinimized}
      />
      
      <MapClickHandler
        key="map-click-handler"
        isSpotModeActive={isSpotModeActive}
        onLocationSelect={onLocationSelect}
        setFocusedEntityId={setFocusedEntityId}
        setIsolatedEntityId={setIsolatedEntityId}
        isImmersiveMode={isImmersiveMode}
        isSearchPanelMinimized={isSearchPanelMinimized}
        setIsSearchPanelMinimized={setIsSearchPanelMinimized}
        userLocation={userLocation}
        spotModeLocation={spotModeLocation}
        setSpotModeLocationSetTime={setSpotModeLocationSetTime}
      />
      
      <MapBoundsFitter 
        key="bounds-fitter"
        markers={markers} 
        userLocation={userLocation} 
        isEntityMode={isEntityMode} 
        radius={radius} 
        isImmersiveMode={isImmersiveMode}
        isSearchPanelMinimized={isSearchPanelMinimized}
        isSpotModeActive={isSpotModeActive}
        panelState={panelState}
        leftPanelWidth={leftPanelWidth}
      />
      
      {enableDragToLoad && (
        <MapDragHandler
          key="drag-handler"
          enabled={enableDragToLoad && !isSpotModeActive}
          fetchNearbyEntities={fetchNearbyEntities}
          reverseGeocode={reverseGeocode}
          setUserLocation={setUserLocation}
          setSearchLocationName={setSearchLocationName}
          setSearchLocation={setSearchLocation}
          saveLocation={saveLocation}
          t={t}
        />
      )}
    </>
  )
})

export default MapControllers
